import type { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  from,
  map,
  mergeAll,
  NEVER,
  share,
  Subject,
  tap,
  throttleTime,
} from 'rxjs';
import { uniq } from 'lodash';

import { SmartSearchService } from '@apps/main/modules/search/smartSearchService';
import type { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import {
  AvailabilityOrder,
  IngredientType,
} from '@libs/trip-planner-interfaces';
import type {
  BusinessCriteria,
  DayTicketRate,
  DestinationAvailabilityCriteria,
  DestinationAvailabilitySearchUserCriteria,
  ItinerarySearchCriteria,
  PortStaticInfo,
  CarrierStaticInfo,
  CarrierName,
  SearchSuggestionCriteria,
  AutoCompleteSuggestionsByCategory,
  AccommodationAvailabilityCriteria,
  ActivityAvailabilityCriteria,
  DetailsStaticInfo,
  HotelConstraintQL,
  OverviewStaticInfoResult,
  HotelIngredientAdapter,
  TransportVehicle,
  TransportAvailabilityUserCriteria,
  TransportListResponse,
  HotelBookableOptionWithRoomsAndDateAndPrices,
  Coordinate,
  IngredientId,
  ActivitySuggestionsResponse,
  ActivityStaticInfo,
  ActivitySuggestionsSearchCriteria,
  ItinerarySuggestions,
  NeighborhoodPolygonsOnlyHotel,
} from '@libs/trip-planner-interfaces';
import { stringComparer, logged, toDictionary } from '@libs/utils';

import { useSuggestionSearchService } from '../search/suggestionSearchService';

import { ItinerarySearchResults } from './itinerarySearchResults';
import { DestinationSearchResults } from './destinationSearchResults';

const SEARCH_SERVICE = new SmartSearchService();
const SUGGESTION_SERVICE = useSuggestionSearchService();

export default class TripPlannerModel {
  public readonly itinerarySearches: Observable<ItinerarySearchCriteria>;
  public readonly itinerarySearchResults: Observable<ItinerarySearchResults>;
  public readonly destinationAvailabilitySearches: Observable<DestinationAvailabilityCriteria>;
  public readonly destinationAvailabilitySearchResults: Observable<DestinationSearchResults>;

  private readonly searchItinerarySubject =
    new Subject<ItinerarySearchCriteria>();
  private readonly searchDestinationvailabilitySubject =
    new Subject<DestinationAvailabilityCriteria>();

  private readonly throttleTimeMiliseconds = 500;

  constructor(
    private statsigEventsStore: ReturnType<typeof useStatsigEventsStore>
  ) {
    this.itinerarySearches = this.searchItinerarySubject.pipe(
      throttleTime(this.throttleTimeMiliseconds, undefined, {
        leading: true,
        trailing: true,
      }),
      distinctUntilChanged(
        (previousCriteria, currentCriteria) =>
          stringComparer(previousCriteria, currentCriteria) === 0
      ),
      logged('search itinerary criteria'),
      share()
    );

    this.itinerarySearchResults = this.itinerarySearches.pipe(
      map(
        (criteria) =>
          new ItinerarySearchResults(
            SEARCH_SERVICE,
            criteria,
            criteria.shouldCancelPreviousOngoingRequest
              ? this.itinerarySearches
              : NEVER
          )
      ),
      logged('search itineraries results'),
      share()
    );

    this.destinationAvailabilitySearches =
      this.searchDestinationvailabilitySubject.pipe(
        throttleTime(this.throttleTimeMiliseconds, undefined, {
          leading: true,
          trailing: true,
        }),
        logged('search destination availability criteria'),
        share()
      );

    this.destinationAvailabilitySearchResults =
      this.destinationAvailabilitySearches.pipe(
        map(
          (criteria) =>
            new DestinationSearchResults(
              SEARCH_SERVICE,
              criteria,
              this.destinationAvailabilitySearches
            )
        ),
        logged('search destination availability results'),
        share()
      );
  }

  public searchItineraries(criteria: ItinerarySearchCriteria): void {
    this.searchItinerarySubject.next({
      userCriteria: criteria.userCriteria,
      businessCriteria: criteria.businessCriteria,
      shouldCancelPreviousOngoingRequest:
        criteria.shouldCancelPreviousOngoingRequest ?? true,
    });
  }

  public searchSuggestionItineraryPrices(
    criteria: ItinerarySearchCriteria
  ): ItinerarySuggestions {
    return SEARCH_SERVICE.searchItinerarySuggestions(criteria);
  }

  public searchDestinationAvailability(
    destinationAvailabilitySearchUserCriteria: DestinationAvailabilitySearchUserCriteria,
    businessCriteria: BusinessCriteria
  ): void {
    this.searchDestinationvailabilitySubject.next({
      userCriteria: destinationAvailabilitySearchUserCriteria,
      businessCriteria: businessCriteria,
    });
  }

  public searchTransportAvailability(
    transportAvailabilityCriteria: TransportAvailabilityUserCriteria,
    businessCriteria: BusinessCriteria
  ): Observable<TransportListResponse> {
    return SEARCH_SERVICE.searchTransportAvailability({
      userCriteria: transportAvailabilityCriteria,
      businessCriteria,
    });
  }

  public searchAccommodationAvailability(
    accommodationAvailabilityCriteria: AccommodationAvailabilityCriteria
  ): Observable<HotelBookableOptionWithRoomsAndDateAndPrices> {
    return SEARCH_SERVICE.searchAccommodationAvailability(
      accommodationAvailabilityCriteria
    );
  }

  public searchActivityAvailability(
    productAvailabilityCriteria: ActivityAvailabilityCriteria
  ): Observable<DayTicketRate[]> {
    return SEARCH_SERVICE.searchActivityAvailability(
      productAvailabilityCriteria
    );
  }

  public searchAreThereActivityStaticInfo(
    constraint: HotelConstraintQL
  ): Observable<boolean> {
    return SEARCH_SERVICE.searchMinimumActivityStaticInfo(constraint);
  }

  public getSuggestedActivities(
    criteria: ActivitySuggestionsSearchCriteria
  ): Observable<ActivitySuggestionsResponse> {
    return from(SEARCH_SERVICE.getActivitySuggestions(criteria));
  }

  public getActivitiesStaticData(
    codes: string[]
  ): Observable<ActivityStaticInfo[]> {
    return SEARCH_SERVICE.getActivitiesStaticInfo(codes);
  }

  public searchDestinationSuggestedActivities(
    constraint: HotelConstraintQL
  ): Observable<ActivityStaticInfo[]> {
    return SEARCH_SERVICE.searchDestinationSuggestedActivities(constraint);
  }

  public searchDetailStaticInfo(
    productCode: string,
    parentIngredientId: IngredientId
  ): Observable<DetailsStaticInfo> {
    return SEARCH_SERVICE.searchDetailsStaticInfo(
      productCode,
      parentIngredientId
    );
  }

  public searchProductOverviewStaticInfo(
    productCode: string,
    ingredientId: IngredientId
  ): Observable<OverviewStaticInfoResult> {
    const hotelAdapter: HotelIngredientAdapter = {
      ingredientType: IngredientType.Hotel,
      ingredientId,
      constraint: {
        hotelCodes: [productCode],
        selectedHotelTypes: [],
        boardBasis: [],
      },
      heartedHotels: [],
      subIngredients: [],
      suggestedBy: AvailabilityOrder.Popular,
    };

    return SEARCH_SERVICE.searchOverviewStaticInfo([hotelAdapter]);
  }

  public searchAutoCompleteSuggestions(
    searchSuggestionCriteria: SearchSuggestionCriteria
  ): Observable<AutoCompleteSuggestionsByCategory[]> {
    return SUGGESTION_SERVICE.search(searchSuggestionCriteria);
  }

  public searchHotelName(inputText: string): Observable<string> {
    return SEARCH_SERVICE.searchHotelName(inputText);
  }

  public searchHotelCode(hotelName: string): Observable<string | undefined> {
    return SEARCH_SERVICE.searchHotelCode(hotelName);
  }

  public searchHotelMainPolygon(
    inputText: string
  ): Observable<{ name?: string; id?: number }> {
    return SEARCH_SERVICE.searchHotelMainPolygon(inputText);
  }

  public searchHotelPosition(
    inputText: string
  ): Observable<Coordinate | undefined> {
    return SEARCH_SERVICE.searchHotelPosition(inputText);
  }

  public searchHotelNeighborhoodPolygons(
    inputText: string
  ): Observable<NeighborhoodPolygonsOnlyHotel[] | undefined> {
    return SEARCH_SERVICE.searchHotelNeighborhoodPolygons(inputText);
  }

  public searchCarrierNames(
    carrierCodes: string[]
  ): Observable<Record<string, CarrierName>> {
    return SEARCH_SERVICE.searchCarrierNames(carrierCodes);
  }

  public searchCarrierStaticInfo(
    carrierCodes: string[]
  ): Observable<Record<string, CarrierStaticInfo>> {
    const carriers = uniq(carrierCodes).map((carrierCode) =>
      SEARCH_SERVICE.searchCarrierStaticInfo(carrierCode)
    );
    return from(carriers).pipe(
      mergeAll(),
      tap((carrierStaticInfo) => {
        if (!carrierStaticInfo.logo) {
          this.statsigEventsStore.logMissingCarrierLogo({
            carrierCode: carrierStaticInfo.code,
          });
        }
      }),
      toDictionary((carrierStaticInfo) => carrierStaticInfo.code)
    );
  }

  public searchAirportStaticInfo(
    iatas: string[]
  ): Observable<Record<string, PortStaticInfo>> {
    const airports = uniq(iatas).map((portCode) =>
      SEARCH_SERVICE.searchAirportStaticInfo(portCode)
    );
    return from(airports).pipe(
      mergeAll(),
      toDictionary((airportStaticInfo) => airportStaticInfo.code)
    );
  }

  public searchSeaportStaticInfo(
    iatas: string[]
  ): Observable<Record<string, PortStaticInfo>> {
    const seaports = uniq(iatas).map((portCode) =>
      SEARCH_SERVICE.searchSeaportStaticInfo(portCode)
    );
    return from(seaports).pipe(
      mergeAll(),
      toDictionary((seaportStaticInfo) => seaportStaticInfo.code)
    );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    return SEARCH_SERVICE.getFerryVehicleTypes();
  }
}
